import React, { useEffect, useMemo, useRef, useState } from "react";
import { Chart } from "react-chartjs-2";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

import SettingsIcon from "@mui/icons-material/Settings";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Button,
  Divider,
  Stack,
  IconButton,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  Popover,
  Typography,
  Checkbox,
  OutlinedInput,
  ListItemText,
} from "@mui/material";

import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { useFundsYieldsByIds } from "../../../hooks/useFunds";
import { MimshakService } from "../../../services/mimshakService";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";
import { colorAdjustedProductType, colorByFundId, colorFund, colorProductType, colorHandlerCode } from "../../../utils/colorEnumFormatter";

import {
    BaseDataset,
    buildBarDataset,
    buildChartOptions,
  buildLineDataset,
  ChartContainer,
  finalValueLabelPlugin,
  formatPeriodLabel,
  selectedFundOutlinePlugin
} from "../../../shared/UIParts/chartUtils";
import { FundDataBasic } from "../../../interfaces/DTO/FundDataBasic";
import { FundGraphControlPanel } from "../../GemelData/FundGraphControlPanel";
import { fundService } from "../../../services/fundService";
import { formatHandlerCode, getHandlerCodeList } from "../../../utils/namesFormater";
import { renderFundTooltip, verticalLinePlugin } from "../../../shared/UIParts/chartTooltipUtils";
import { formatFundFieldLabel, FundFieldSection, getSubspecializationColor, getStockExposureRange, getForeignExposureRange, getCurrencyExposureRange, stockExposureColors, foreignExposureColors, currencyExposureColors, stockExposureRanges, foreignExposureRanges, currencyExposureRanges } from "../../../utils/fundsFormatter";
import { FinancialCalculatorService } from "../../../services/financialCalculatorService";
import { direction } from "html2canvas/dist/types/css/property-descriptors/direction";
import { GemelDataFilteredView } from "../../GemelData/types";
import FundAccordionList from "./FundAccordionList";


ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  verticalLinePlugin,
  finalValueLabelPlugin,
  selectedFundOutlinePlugin
);

type ColorByOption = "manufacturer" | "sub_specialization" | "fund_classification" | "product_type" | "fund_id" | "stock_exposure" | "foreign_exposure" | "currency_exposure";


interface ProductFundYieldsChartProps {
  products?: Product[];
  fundPathCodes?: number[];
  defaultColorBy?: ColorByOption;
  colorBy?: ColorByOption; // controlled override — syncs internalColorBy without feedback loop
  defaultMonthsBack?: number;
  defaultField?: keyof FundDataBasic;
  yAxisField?: keyof FundDataBasic; // controlled override — syncs internalSelectedFields without feedback loop
  productType?: number;
  // תמיכה חדשה ב-GemelDataFilteredView
  filters?: GemelDataFilteredView;
  onFiltersChange?: (filters: GemelDataFilteredView) => void;
  totalFundsCount?: number;
  onDisplayedFundIdsChange?: (ids: number[]) => void;
}

export const ProductFundYieldsChart: React.FC<ProductFundYieldsChartProps> = ({
  products,
  fundPathCodes,
  defaultColorBy,
  colorBy: colorByProp,
  defaultMonthsBack,
  defaultField,
  yAxisField: yAxisFieldProp,
  productType,
  filters,
  onFiltersChange,
  totalFundsCount,
  onDisplayedFundIdsChange,
}) => {
  // State פנימי - backward compatibility
  const [internalColorBy, setInternalColorBy] = useState<ColorByOption>(defaultColorBy ?? "product_type");
  const [internalChartType, setInternalChartType] = useState<"line" | "bar">("line");
  const [internalMonthsBack, setInternalMonthsBack] = useState<number>(defaultMonthsBack ?? 11);
  const [internalSelectedFields, setInternalSelectedFields] = useState<(keyof FundDataBasic)[]>([defaultField ?? "cumulative_yield_dynamic"]);

  // State שמשותף תמיד
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const chartRef = useRef<ChartJS | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFunds, setSelectedFunds] = useState<FundDataBasic[]>([]);
  const [visibleFundIds, setVisibleFundIds] = useState<number[]>([]);
  const [yields, setYields] = useState<any[]>([]);

  // ערכים אפקטיביים - עדיפות ל-filters אם קיים, אחרת state פנימי
  const colorBy = internalColorBy;
  const chartType = internalChartType;
  const monthsBack = filters?.yAxisField ? internalMonthsBack : (internalMonthsBack);
  const selectedFields = filters?.yAxisField ? [filters.yAxisField] : internalSelectedFields;

  const [initialAmount, setInitialAmount] = useState<number>(100000);
  const [customDateStr, setCustomDateStr] = useState<string>("");
  const [useCustomDate, setUseCustomDate] = useState<boolean>(false);
   const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
 const [showAmounts, setShowAmounts] = useState<boolean>(false);
  const [selectedFundId, setSelectedFundId] = useState<number | null>(null);
  const [clickedFundIds, setClickedFundIds] = useState<number[]>([]);

  // State לפילטור לפי צביעה
  const [selectedSubSpecializations, setSelectedSubSpecializations] = useState<string[]>([]);
  const [selectedManagingCorporations, setSelectedManagingCorporations] = useState<string[]>([]);
  const [selectedStockExposureRanges, setSelectedStockExposureRanges] = useState<string[]>([]);
  const [selectedForeignExposureRanges, setSelectedForeignExposureRanges] = useState<string[]>([]);
  const [selectedCurrencyExposureRanges, setSelectedCurrencyExposureRanges] = useState<string[]>([]);

  const policies = products?.flatMap((p) => p.policies_or_accounts) ?? [];
  const initialFundIds: number[] = products
    ? MimshakService.extractPathCodes(policies)
    : fundPathCodes ?? [];
  const fundIds: number[] = Array.from(new Set([...initialFundIds, ...selectedFunds.map((f) => f.fund_id)]));

  // אם לא מועברים fundIds אבל יש productType, טען את כל הקרנות מסוג המוצר
  const funds = fundIds.length > 0
    ? fundService.getFundsFromCacheByIds(fundIds)
    : productType
      ? fundService.getFundsFromCacheByCriteria({ productType })
      : [];

  const selectedSection:FundFieldSection = formatFundFieldLabel(selectedFields[0]).section;

  // חישוב סכום לכל קרן מהמוצרים
  const fundAmountMap = useMemo(() => {
    const amountMap = new Map<number, number>();
    if (!products) return amountMap;

    products.forEach((product) => {
      product.policies_or_accounts.forEach((policy) => {
        policy.investment_paths.forEach((path) => {
          const fundId = path.path_code;
          const amount = path.accumulated_amount || 0;
          const currentAmount = amountMap.get(fundId) || 0;
          amountMap.set(fundId, currentAmount + amount);
        });
      });
    });

    return amountMap;
  }, [products]);

  // מציאת כל ערכי sub_specialization הייחודיים
  const allSubSpecializations = useMemo(() => {
    const uniqueValues = [...new Set(funds.map(f => f.sub_specialization || '-'))];
    return uniqueValues.sort();
  }, [funds]);

  // מציאת כל יצרני הקרנות הכלליים
  const generalManagingCorporations = useMemo(() => {
    const handlerList = getHandlerCodeList();
    return handlerList.filter(handler => handler.isGeneral);
  }, []);

  // מיפוי צבעים לsub_specialization - שימוש בפונקציה שמבטיחה קונסיסטנטיות
  const subspecializationColorMap = useMemo(() => {
    const colorMap: { [key: string]: string } = {};
    allSubSpecializations.forEach((spec) => {
      colorMap[spec] = getSubspecializationColor(spec);
    });
    return colorMap;
  }, [allSubSpecializations]);

  // סנכרון internalSelectedFields עם filters.yAxisField
  useEffect(() => {
    if (filters?.yAxisField) {
      setInternalSelectedFields([filters.yAxisField]);
    } else if (defaultField) {
      setInternalSelectedFields([defaultField]);
    }
  }, [filters?.yAxisField, defaultField]);

  useEffect(() => {
   const initialIds = products
      ? MimshakService.extractPathCodes(products.flatMap((p) => p.policies_or_accounts))
      : fundPathCodes ?? [];

    let updatedFunds: FundDataBasic[];
    if (initialIds.length > 0) {
      updatedFunds = fundService.getFundsFromCacheByIds(initialIds);
    } else if (productType) {
      updatedFunds = fundService.getFundsFromCacheByCriteria({ productType });
    } else {
      updatedFunds = [];
    }

    setSelectedFunds(updatedFunds);
    setVisibleFundIds(updatedFunds.map(f => f.fund_id));
  }, [products, fundPathCodes, productType]);
  const latestReportDate = useMemo(() => {
    const sample = funds.find((f) => f.report_period);
    if (!sample?.report_period) return new Date();
    const period = sample.report_period.toString();
    const year = parseInt(period.slice(0, 4));
    const month = parseInt(period.slice(4, 6));
    return new Date(year, month - 1);
  }, [funds]);
  useEffect(() => {
    const fetchData = async () => {
      const data = await fundService.fetchFundsCustomFieldsByIds(fundIds, selectedFields, monthsBack);
      setYields(data);
    };
    fetchData();
  }, [selectedFields, monthsBack, fundIds.join(',')]);
  useEffect(() => {
    if (funds && funds.length > 0 && selectedFunds.length === 0) {
      setSelectedFunds(funds);
    }
  }, [funds]);

  useEffect(() => {
    if (defaultField) {
      setInternalSelectedFields([defaultField]);
    }
  }, [defaultField]);

  useEffect(() => {
    onDisplayedFundIdsChange?.(fundIds);
  }, [fundIds.join(',')]);

  useEffect(() => {
    if (colorByProp) {
      setInternalColorBy(colorByProp);
    } else if (defaultColorBy) {
      setInternalColorBy(defaultColorBy);
    }
  }, [colorByProp, defaultColorBy]);

  useEffect(() => {
    if (yAxisFieldProp) {
      setInternalSelectedFields([yAxisFieldProp]);
    }
  }, [yAxisFieldProp]);
  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(customDateStr)) {
      const [year, month] = customDateStr.split("-").map(Number);
      const today = new Date();
      const selectedDate = new Date(year, month - 1);
      const months =
        (today.getFullYear() - selectedDate.getFullYear()) * 12 +
        today.getMonth() - selectedDate.getMonth() + 1;
      setInternalMonthsBack(months);
      setUseCustomDate(true);
    }
  }, [customDateStr]);

 
  const handleToggleFundVisibility = (fundId: number) => {
    setVisibleFundIds((prev) =>
      prev.includes(fundId)
        ? prev.filter((id) => id !== fundId)  // מוסר
        : [...prev, fundId] // מוסיף
    );
  };
const isAmountAllowedForField = (field: keyof FundDataBasic, showAmounts: boolean): boolean => {
          const section = formatFundFieldLabel(field).section as FundFieldSection;
          return showAmounts && [
            FundFieldSection.MOVING_YIELD,
            FundFieldSection.COMMULATIVE_YIELD,
            FundFieldSection.YIELD,
          ].includes(section);
        };
  const chartData = React.useMemo(() => {
    if (!yields) return null;
    if (!funds || funds.length === 0) return null;
    const labels = Array.from(new Set(yields.map((y) => y.report_period))).sort();

    const fundYieldMap: Record<string, Record<string, number>> = {};

    yields.forEach(y => {
      const fundId = y.fund_id?.toString() || "unknown";
      if (!fundYieldMap[fundId]) fundYieldMap[fundId] = {};
      fundYieldMap[fundId][y.report_period ?? ""] = y[selectedFields[0]] ?? 0;
    });

    const groupedByFund: Record<string, number[]> = {};
   
    Object.entries(fundYieldMap).forEach(([fundId, yieldsByPeriod]) => {
      const values = labels.map((label) => {
      const isAmountAllowed = isAmountAllowedForField(selectedFields[0], showAmounts);
      const rawValue = yieldsByPeriod[label] as any;

      // ✅ שמירה על null אמיתי
      if (rawValue === undefined || rawValue === null || rawValue === "null") return null;

      const numericValue = parseFloat(rawValue);
      if (isNaN(numericValue)) return null;

      return showAmounts && isAmountAllowed
        ? initialAmount * (1 + numericValue / 100)
        : numericValue;
    });

    // console.log("Fund", fundId, values);
      groupedByFund[fundId] = values;
    });
    
     const getFinalValueLabel = ({
        field,
        data,
        showAmounts,
      }: {
        field: keyof FundDataBasic;
        data: number[];
        showAmounts: boolean;
      }): string | undefined => {
       const section = formatFundFieldLabel(field).section;
       const raw = data?.[data.length - 1];
       const value = Number(raw);
          if (isNaN(value)) return undefined;
       if (section === FundFieldSection.RANKING) {
          return `# ${Math.round(value)}`;
       }
        if (section === FundFieldSection.OTHER || section === FundFieldSection.QUALITY) {
          return `${value.toFixed(2)}`;
       }
        const isAmount = isAmountAllowedForField(field, showAmounts); 
        return isAmount ? Math.round(value).toLocaleString() : `${value.toFixed(2)}%`;
      };

    const datasets = Object.entries(groupedByFund).map(([fundId, data]) => {
        const numericFundId = Number(fundId);
        const fund = funds?.find((f) => f.fund_id === numericFundId);
        
        if (!funds || funds.length === 0) return null;
     
        const product = products?.find((p) =>
        p.policies_or_accounts.some((pol) =>
          pol.investment_paths.some((ip) => ip.path_code === numericFundId)
        )
      );
      
        let backgroundColor = "rgba(150, 150, 150, 0.7)";
        let shouldShow = true; // בדיקה אם צריך להציג את הקרן בהתאם לפילטר

       if (colorBy === "product_type" && product) {
        const type = product.details.adjusted_product_type ?? product.details.product_type;
        const baseColor = colorAdjustedProductType(type);
        backgroundColor = colorFund(numericFundId, baseColor);
      } else if (colorBy === "fund_id") {
        backgroundColor = colorByFundId(numericFundId);
      } else if (colorBy === "sub_specialization" && fund) {
        const subspecialization = fund.sub_specialization || '-';
        shouldShow = selectedSubSpecializations.length === 0 || selectedSubSpecializations.includes(subspecialization);
        backgroundColor = shouldShow ? subspecializationColorMap[subspecialization] : 'rgba(200,200,200,0.3)';
      } else if (colorBy === "manufacturer" && fund) {
        const managingCorp = fund.managing_corporation_legal_id ? String(fund.managing_corporation_legal_id) : '-';
        shouldShow = selectedManagingCorporations.length === 0 || selectedManagingCorporations.includes(managingCorp);
        backgroundColor = shouldShow && fund.managing_corporation_legal_id
          ? colorHandlerCode(fund.managing_corporation_legal_id)
          : shouldShow
          ? 'rgb(170,170,170)'
          : 'rgba(200,200,200,0.3)';
      } else if (colorBy === "stock_exposure" && fund) {
        const stockExposure = fund.stock_market_percent ?? 0;
        const exposureRange = getStockExposureRange(stockExposure);
        shouldShow = selectedStockExposureRanges.length === 0 || selectedStockExposureRanges.includes(exposureRange);
        backgroundColor = shouldShow ? stockExposureColors[exposureRange] : 'rgba(200,200,200,0.3)';
      } else if (colorBy === "foreign_exposure" && fund) {
        const foreignExposure = fund.foreign_exposure_percent ?? 0;
        const exposureRange = getForeignExposureRange(foreignExposure);
        shouldShow = selectedForeignExposureRanges.length === 0 || selectedForeignExposureRanges.includes(exposureRange);
        backgroundColor = shouldShow ? foreignExposureColors[exposureRange] : 'rgba(200,200,200,0.3)';
      } else if (colorBy === "currency_exposure" && fund) {
        const currencyExposure = fund.foreign_currency_exposure_percent ?? 0;
        const exposureRange = getCurrencyExposureRange(currencyExposure);
        shouldShow = selectedCurrencyExposureRanges.length === 0 || selectedCurrencyExposureRanges.includes(exposureRange);
        backgroundColor = shouldShow ? currencyExposureColors[exposureRange] : 'rgba(200,200,200,0.3)';
      } else if (fund) {
        let categoryKey: string | number = fund.fund_id;
          if (colorBy === "fund_classification") categoryKey = fund.fund_classification || "לא ידוע";

          const hash = String(categoryKey).split("").reduce((acc, c) => acc + c.charCodeAt(0), 0);
          const r = 100 + (hash * 17) % 100;
          const g = 100 + (hash * 31) % 100;
          const b = 100 + (hash * 47) % 100;
          backgroundColor = `rgba(${r}, ${g}, ${b}, 0.7)`;
        }
      
        const fundYields = yields.filter(y => y.fund_id === numericFundId);
        // let lastCumulative = fundYields[fundYields.length - 1]?.cumulative_12m_yield;
        // const lastYieldEntry = fundYields[fundYields.length - 1];
        // if (monthsBack < 12) lastCumulative = lastYieldEntry?.year_to_date_yield ?? 0;
        // else if (monthsBack == 12) lastCumulative = lastYieldEntry?.cumulative_12m_yield ?? 0;
        // else if (monthsBack <= 36) lastCumulative = lastYieldEntry?.yield_trailing_3_yrs ?? 0;
        // else if (monthsBack <= 60) lastCumulative = lastYieldEntry?.yield_trailing_5_yrs ?? 0;
        // else lastCumulative = NaN; // לא נתמך
      
         const isHidden =!fund || !visibleFundIds.includes(fund.fund_id) ;
      
        const baseDataset: BaseDataset = {
            label:  `מסלול ${fundId}`,
            data: data,
            fundId: numericFundId,
            spanGaps: false, //כדי ש־Chart.js לא "יחבר" בין נקודות סביב null, הוסיפי ל־dataset את ההגדרה
            backgroundColor: backgroundColor,
            borderColor: backgroundColor,
            product: product ?? undefined,
            finalValueLabel: chartType === "line"
              ? getFinalValueLabel({
                  field: selectedFields[0],
                  data: data,
                  showAmounts: showAmounts
                })
              : undefined,
           hidden: isHidden,
        
          };
      
        return chartType === "line" ? buildLineDataset(baseDataset) : buildBarDataset(baseDataset);
      });
      

    return { labels: labels.map(formatPeriodLabel), datasets };
  }, [yields, funds, colorBy, chartType, products, visibleFundIds, selectedSubSpecializations, selectedManagingCorporations, selectedStockExposureRanges, selectedForeignExposureRanges, selectedCurrencyExposureRanges, subspecializationColorMap, showAmounts]);

  const chartOptions = React.useMemo(() => {
    const options = buildChartOptions({
      isLineChart: chartType === "line",
      isStacked: false,
      onDatasetClick: (dataset, _dataIndex) => {
         // הדגש את כל הנקודות של fundId זה
         if (dataset.fundId) {
           setSelectedFundId(prev => prev === dataset.fundId ? null : dataset.fundId);

           // הוסף/הסר את הקרן מרשימת הקרנות שנלחצו
           setClickedFundIds(prev => {
             if (prev.includes(dataset.fundId)) {
               return prev.filter(id => id !== dataset.fundId);
             } else {
               return [...prev, dataset.fundId];
             }
           });
         }
         // פתח גם את המגירה אם יש product
         if (products && dataset.product) setSelectedProduct(dataset.product);
      },
     tooltip: {
        enabled: false,
        external: (context: any) =>
          renderFundTooltip({ chart: context.chart, tooltip: context.tooltip,funds: funds,
            selectedField: selectedFields[0],
            positionOutsideCanvas: true,
            positionDirection: "left",}),
        },

    }) as any;

    // העבר את ה-selectedFundId לפלאגין
    options.selectedFundId = selectedFundId;

    // הוסף מטפל ללחיצה על איזור ריק כדי לבטל בחירה
    const originalOnClick = options.onClick;
    options.onClick = (event: any, elements: any[], chart: any) => {
      // אם לחצו על איזור ריק (ללא נקודות)
      if (elements.length === 0) {
        setSelectedFundId(null);
      } else if (originalOnClick) {
        // אחרת קרא למטפל המקורי
        originalOnClick(event, elements, chart);
      }
    };

    // הסתר legend בדיפולט
    options.plugins = options.plugins || {};
    options.plugins.legend = { ...options.plugins.legend, display: false };

    // הוסף כותרות לצירים
    options.scales = options.scales || {};
    options.scales.x = {
      ...options.scales.x,
      title: {
        display: true,
        text: 'תקופה',
        font: {
          size: 14,
          weight: 'bold' as const,
        },
      },
    };
    options.scales.y = {
      ...options.scales.y,
      title: {
        display: true,
        text: formatFundFieldLabel(selectedFields[0]).label,
        font: {
          size: 14,
          weight: 'bold' as const,
        },
      },
    };

    return options;
  }, [funds, setSelectedProduct, chartType, selectedFields, fundPathCodes, products, selectedFundId]);
  
  const handleChartTypeChange = (_: any, newType: "bar" | "line" | null) => {
    if (newType) {
      console.log("Chart type changed to", newType);
      setInternalChartType(newType);
    }
  };
  const handleColorByChange = (event: SelectChangeEvent) => {
    const newValue = event.target.value as ColorByOption;
    setInternalColorBy(newValue);

    // נקה selections כשמשנים את סוג הצביעה
    if (newValue !== 'sub_specialization') {
      setSelectedSubSpecializations([]);
    }
    if (newValue !== 'manufacturer') {
      setSelectedManagingCorporations([]);
    }
    if (newValue !== 'stock_exposure') {
      setSelectedStockExposureRanges([]);
    }
    if (newValue !== 'foreign_exposure') {
      setSelectedForeignExposureRanges([]);
    }
    if (newValue !== 'currency_exposure') {
      setSelectedCurrencyExposureRanges([]);
    }
  };

  const handleModeButtonClick = (mode: 'monthly' | 'cumulative') => {
    const newField: keyof FundDataBasic = mode === 'monthly' ? 'monthly_yield' : 'cumulative_yield_dynamic';
    const newChartType: "line" | "bar" = mode === 'monthly' ? 'bar' : 'line';

    setInternalChartType(newChartType);
    setInternalSelectedFields([newField]);

    // עדכן filters אם קיים
    if (filters && onFiltersChange) {
      const updatedFilters = { ...filters, yAxisField: newField };
      onFiltersChange(updatedFilters);
    }
  };
  const handleCustomDateBlur = () => {
      const match = customDateStr.match(/^(\d{2})(\d{4})$/);
      if (!match) return;
      const [, mm, yyyy] = match;
      const customDate = new Date(Number(yyyy), Number(mm) - 1);
      // הנחה: יש משתנה latestReportDate מסוג Date
      const diffMonths = (latestReportDate.getFullYear() - customDate.getFullYear()) * 12 + (latestReportDate.getMonth() - customDate.getMonth());
      if (diffMonths >= 0) {
        setInternalMonthsBack(diffMonths);
      }
  };

  // Handler לשינוי שדות - תומך ב-filters
  const handleFieldsChange = (newFields: (keyof FundDataBasic)[]) => {
    setInternalSelectedFields(newFields);

    // עדכן filters אם קיים
    if (filters && onFiltersChange && newFields.length > 0) {
      const updatedFilters = { ...filters, yAxisField: newFields[0] };
      onFiltersChange(updatedFilters);
    }
  };
 
  return (
    <Box>
      {totalFundsCount !== undefined && (
        <Box
          sx={{
            mb: 2,
            p: 1,
            bgcolor: totalFundsCount > (fundPathCodes?.length ?? 0) ? 'warning.light' : 'info.light',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}
        >
          {totalFundsCount > (fundPathCodes?.length ?? 0) && (
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>⚠️</Typography>
          )}
          <Typography variant="body2">
            מציג {fundPathCodes?.length ?? 0} קרנות מתוך {totalFundsCount}
            {totalFundsCount > (fundPathCodes?.length ?? 0) && ' (מוגבל ל-50 קרנות בתצוגה)'}
          </Typography>
        </Box>
      )}

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 3 }}>
        <Box>
          <FormControl dir="rtl" sx={{ minWidth: 220 }} size="small">
            <InputLabel id="color-by-label">צביעה לפי</InputLabel>
            <Select
              labelId="color-by-label"
              value={colorBy}
              label="צביעה לפי"
              onChange={handleColorByChange}
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              <MenuItem value="manufacturer" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>יצרן</MenuItem>
              <MenuItem value="sub_specialization" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>תת־התמחות</MenuItem>
              <MenuItem value="product_type" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>סוג מוצר</MenuItem>
              <MenuItem value="fund_id" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>מסלול (קרן)</MenuItem>
              <MenuItem value="stock_exposure" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>חשיפה למניות</MenuItem>
              <MenuItem value="foreign_exposure" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>חשיפה לחו"ל</MenuItem>
              <MenuItem value="currency_exposure" sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>חשיפה למט"ח</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Sub-Specialization Multi-Select */}
        {colorBy === 'sub_specialization' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>התמחויות</InputLabel>
            <Select
              multiple
              value={selectedSubSpecializations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedSubSpecializations(value);
              }}
              input={<OutlinedInput label="התמחויות" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {allSubSpecializations.map((spec) => (
                <MenuItem key={spec} value={spec} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedSubSpecializations.indexOf(spec) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: subspecializationColorMap[spec],
                      }}
                    />
                    <ListItemText primary={spec} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Managing Corporation Multi-Select */}
        {colorBy === 'manufacturer' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>יצרנים</InputLabel>
            <Select
              multiple
              value={selectedManagingCorporations}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedManagingCorporations(value);
              }}
              input={<OutlinedInput label="יצרנים" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {generalManagingCorporations.map((handler) => (
                <MenuItem key={handler.code} value={handler.code} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedManagingCorporations.indexOf(handler.code) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: colorHandlerCode(handler.code),
                      }}
                    />
                    <ListItemText primary={handler.name} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Stock Exposure Multi-Select */}
        {colorBy === 'stock_exposure' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>טווחי חשיפה למניות</InputLabel>
            <Select
              multiple
              value={selectedStockExposureRanges}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedStockExposureRanges(value);
              }}
              input={<OutlinedInput label="טווחי חשיפה למניות" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {stockExposureRanges.map((range) => (
                <MenuItem key={range} value={range} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedStockExposureRanges.indexOf(range) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: stockExposureColors[range],
                      }}
                    />
                    <ListItemText primary={`${range}%`} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Foreign Exposure Multi-Select */}
        {colorBy === 'foreign_exposure' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>טווחי חשיפה לחו"ל</InputLabel>
            <Select
              multiple
              value={selectedForeignExposureRanges}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedForeignExposureRanges(value);
              }}
              input={<OutlinedInput label="טווחי חשיפה לחו&quot;ל" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {foreignExposureRanges.map((range) => (
                <MenuItem key={range} value={range} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedForeignExposureRanges.indexOf(range) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: foreignExposureColors[range],
                      }}
                    />
                    <ListItemText primary={`${range}%`} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {/* Currency Exposure Multi-Select */}
        {colorBy === 'currency_exposure' && (
          <FormControl size="small" sx={{ minWidth: 250 }}>
            <InputLabel>טווחי חשיפה למט"ח</InputLabel>
            <Select
              multiple
              value={selectedCurrencyExposureRanges}
              onChange={(e) => {
                const value = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                setSelectedCurrencyExposureRanges(value);
              }}
              input={<OutlinedInput label="טווחי חשיפה למט&quot;ח" />}
              renderValue={(selected) =>
                selected.length === 0 ? 'הכל' : `${selected.length} נבחרו`
              }
              MenuProps={{ sx: { '& .MuiMenu-paper': { direction: 'rtl' } } }}
            >
              {currencyExposureRanges.map((range) => (
                <MenuItem key={range} value={range} sx={{ direction: 'rtl', justifyContent: 'flex-start' }}>
                  <Checkbox checked={selectedCurrencyExposureRanges.indexOf(range) > -1} />
                  <Box display="flex" alignItems="center" gap={1} sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: currencyExposureColors[range],
                      }}
                    />
                    <ListItemText primary={`${range}%`} />
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <ToggleButtonGroup
          value={chartType}
          exclusive
          onChange={handleChartTypeChange}
          size="small"
        >
          <ToggleButton value="bar">Bar</ToggleButton>
          <ToggleButton value="line">Line</ToggleButton>
        </ToggleButtonGroup>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" sx={{ mb: 2, mt: 3 }}>
        <Button
          variant={selectedFields[0] === 'monthly_yield' ? "contained" : "outlined"}
          onClick={() => handleModeButtonClick('monthly')}
        >
          תשואה חודשית →
        </Button>
        <Button
          variant={selectedSection === FundFieldSection.COMMULATIVE_YIELD ? "contained" : "outlined"}
          onClick={() => handleModeButtonClick('cumulative')}
        >
          תשואה מצטברת →
        </Button>
      </Stack>
      </Stack>
      <Stack direction="row" spacing={1} alignItems="left" sx={{ mb: 2 }}>
        {[
          { label: "YTD", value:  latestReportDate.getMonth() },
          { label: "1Y", value: 11 },
          { label: "3Y", value: 35 },
          { label: "5Y", value: 59 },
          { label: "10Y", value: 119 },
        ].map(({ label, value }) => (
          <Button
            key={label}
            variant={monthsBack === value ? "contained" : "outlined"}
            onClick={() => setInternalMonthsBack(value)}
          >
            {label}
          </Button>
        ))}
         <Button
          variant={useCustomDate ? "contained" : "outlined"}
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<CalendarMonthIcon />}
        >
       
        </Button>
    <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
           <Box p={2}>
        <TextField
          label="בחר תאריך"
          value={customDateStr}
          onChange={(e) => setCustomDateStr(e.target.value)}
          onBlur={handleCustomDateBlur}
          placeholder="MMYYYY"
          inputProps={{ maxLength: 6 }}
        />
      </Box>
        </Popover>
       
        <IconButton onClick={() => setDrawerOpen(true)} >
        <SettingsIcon />
      </IconButton>
     {![FundFieldSection.YIELD, FundFieldSection.COMMULATIVE_YIELD].includes(selectedSection) && (
      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
        {selectedSection}
        <br />
        {formatFundFieldLabel(selectedFields[0]).label}
      </Typography>
    )}
      
      </Stack>
     
      <ChartContainer height={440} >
        {chartData && <Chart ref={chartRef} 
        type={chartType}
         data={chartData as any}
          options={chartOptions} 
           />}
      </ChartContainer>
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
      {drawerOpen && (
        <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        ModalProps={{ BackdropProps: { invisible: true } }}>
          <Box width={560}>
            <FundGraphControlPanel
              selectedFunds={selectedFunds}
              onChangeFunds={setSelectedFunds}
              selectedFields={selectedFields}
              onChangeFields={handleFieldsChange}
              onToggleFundVisibility={handleToggleFundVisibility}
              isFundVisible={(id) => visibleFundIds.includes(id)}
              initialFundIds={initialFundIds}
              initialAmount={initialAmount}
              onChangeInitialAmount={setInitialAmount}
               showAmounts={showAmounts}
              onToggleShowAmounts={setShowAmounts}
            />
          </Box>
        </Drawer>
      )}
</Drawer>
      {products && selectedProduct && (
        <InsuranceProductDrawer
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      {/* הצגת FundAccordionList עבור קרנות שנלחצו */}
      {clickedFundIds.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <FundAccordionList
            title="קרנות שנבחרו:"
            fundIds={clickedFundIds}
            funds={funds}
            borderColor="rgba(236, 72, 153, 1)"
            isVisible={true}
            onToggleVisibility={() => {}}
            selectedFundIds={clickedFundIds}
            onRemoveFund={(id) => {
              setClickedFundIds(prev => prev.filter(x => x !== id));
              // בטל גם את ההדגשה אם זו הקרן המודגשת
              if (selectedFundId === id) {
                setSelectedFundId(null);
              }
            }}
            fundAmountMap={fundAmountMap}
          />
        </Box>
      )}
    </Box>
  );
};
